import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { syncFormControlDisabledState } from 'common/utils/form-control.util';

export interface Option {
    name: string;
    value: string;
    disabled?: boolean;
    usageType?: (string)[] | null;
}

export enum SelectionSortDirection {
    ASCENDING = 'asc',
    DESCENDING = 'desc',
}

export interface Group {
    parent: string;
    children: Option[];
}

const DEFAULT_EMPTY_VALUE = {
    name: '',
    value: ''
};

@Component({
    selector: 'opt-group-select',
    templateUrl: './opt-group-select.component.html',
    styleUrls: ['./opt-group-select.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class OptGroupSelectComponent implements AfterViewInit, OnChanges {
  @Input() selectId: string;
  @Input() control: string;
  @Input() group: UntypedFormGroup = new UntypedFormGroup({});
  @Input() defaultOption?: string;
  @Input() multiple: boolean = false;
  @Input() variant: 'primary' | 'default' = 'default';
  @Input() size: 'default' | 'small' = 'default';
  @Input() shadow: 'light' | 'default' | 'none' = 'default';
  @Input() sort: SelectionSortDirection;
  @Input() required: boolean;
  @Input() placeholder: string;
  @Input() error: boolean;
  @Input() errorMessage: string | null;
  @Input() infoMessage: string;
  @Input() disabled: boolean | false;
  @Output() onChange: EventEmitter<Option[]> = new EventEmitter();

  isOpen: boolean;
  options$ = new BehaviorSubject<Group[]>([]);

  public minimizeLabel: boolean;
  public valueText: string;
  public defaultEmptyVal: { name: string, value: string } = DEFAULT_EMPTY_VALUE;

  constructor(
      private cdr: ChangeDetectorRef
  ) {
  }

  get options() {
      return this.options$.getValue();
  }

  @Input()
  set options(value: Group[]) {
      if (this.sort) {
          let sortedGroups = value?.slice().sort((groupA, groupB) => groupA.parent.toLowerCase().localeCompare(groupB.parent.toLowerCase()));
          if (this.sort === SelectionSortDirection.DESCENDING) {
              sortedGroups = sortedGroups?.slice().reverse();
          }
          this.options$.next(sortedGroups);

          return;
      }

      this.options$.next(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes.disabled && this.group && this.control) {
          this.syncDisabledState();
      }
  }

  ngAfterViewInit() {
      this.syncDisabledState();
      this.defaultEmptyVal = {
          name: this.placeholder,
          value: ''
      };
      this.minimizeLabel = this.group.controls[this.control].value.length !== 0;
      this.cdr.detectChanges();
      queueMicrotask(() => {
          this.valueText = this.setValueText(this.group.controls[this.control].value, this.multiple);
          this.cdr.detectChanges();
      });

      this.group.controls[this.control].valueChanges.subscribe((change: string) => {
          this.minimizeLabel = change !== '';

          this.valueText = this.setValueText(change, this.multiple);
          this.cdr.detectChanges();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValueText(value: any, multiple: boolean) {
      const controlValue = value;
      this.onChange.emit(controlValue);

      if (multiple) {
          if (
              this.group.controls[this.control].value.length === 1 &&
              this.group.controls[this.control].value[0].value === ''
          ) {
              this.minimizeLabel = false;

              return '';
          }

          return Object.keys(controlValue).length ?
              controlValue.filter((item: Option) => item.value && item.value !== '').map((item: Option) => item.name).filter(Boolean).join(',') :
              '';
      }

      if (controlValue && controlValue[0]?.value === '') {
          this.minimizeLabel = false;

          return '';
      }

      return controlValue && controlValue[0] ? controlValue[0].name || controlValue : '';
  }

  handleClick(close?: boolean) {
      if (this.disabled) {
          return;
      }

      if (close) {
          this.isOpen = false;
      }
      else {
          this.isOpen = !this.isOpen;
      }

      this.cdr.detectChanges();
  }

  handleOptGroupClick(event: Event) {
      if (event.target instanceof HTMLOptionElement) {
          event.stopPropagation();

          return;
      }

      this.group.controls[this.control].setValue(this.multiple ? [] : null);
      this.valueText = '';
      this.minimizeLabel = false;
      this.onChange.emit([]);
      event.stopPropagation();
  }

  private syncDisabledState(): void {
      syncFormControlDisabledState(this.group?.get(this.control), this.disabled);
  }
}
